import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Badge, Box, Flex, Hide } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { themeGet } from 'styled-system';

import getNights from '@shared/utils/getNights';
import ToggleContent from '@shared/v2/components/ToggleContent';
import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import { useDataLayerContext } from '@shared/v2/contexts/DataLayerContext';
import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import { getCampaignEndMessage } from '@shared/v2/utils/getCampaignEndMessage';
import getPackageOptions from '@shared/v2/utils/getPackageOptions';

import Button from '@/components/Button';
import DealInclusions from '@/components/DealInclusions';
import {
  propertyCardCtaDataLayerEvent,
  propertyCardViewAllOptionsDataLayerEvent,
  propertyCardViewPackageDetailsDataLayerEvent,
} from '@/components/DestinationPage/utils/dataLayer';
import Divider from '@/components/Divider';
import LinkButton from '@/components/LinkButton';
import PackageHeroSummary from '@/components/PackageHeroSummary';
import PricePerTraveller from '@/components/PricePerTraveller';
import PromoPill from '@/components/PromoPill';
import ReadMore from '@/components/ReadMore';
import withSkeleton from '@/components/Skeleton/withSkeleton';
import Text from '@/components/Text';
import ViewDetailsModal from '@/components/ViewDetailsModal';
import { TILE_TYPE } from '@/constants';
import { Tile } from '@/propTypes/Campaign';
import { SearchQuery } from '@/propTypes/SearchQuery';
import ImageGallery from '@/shared/components/ImageGallery/ImageGallery';
import { prependFlightCampaignInclusion } from '@/utils/campaigns';
import getPropertyLink from '@/utils/getPropertyLink';
import AllPackageOptionsModal from '@/v2/components/ui/AllPackageOptionsModal';
import ProcuredOfferFooter from '@/v2/components/ui/ProcuredOffer/components/ProcuredOfferFooter';
import PropertyTitle from '@/v2/components/ui/PropertyTitle';
import { usePackagePageV2Analytics } from '@/v2/hooks/featureFlags';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import createFlightCampaignInclusion from '@/v2/utils/campaign/createFlightCampaignInclusion';
import { dataLayer } from '@/v2/utils/dataLayer';
import { getOriginName } from '@/v2/utils/getOriginName';

const StyledPriceTotal = styled.div`
  color: ${themeGet('colors.primary')};
`;

const StyledPromoPillWrapper = styled.div`
  position: absolute;
  top: -20px;
  z-index: 1;
`;

const PropertyCard = ({
  campaignPromoCode,
  priceForAdult = false,
  hidePerPersonPrice = false,
  hidePriceSubtext = false,
  tile,
  flightBadgeText,
  flightBadgeTerms,
  searchQuery,
  Skeleton,
}) => {
  const shouldUseNewEvents = useGA4Events();
  const {
    data: { originCode },
  } = usePackageSearch();
  const { push, query } = useRouter();
  const { pageName } = useDataLayerContext() || {};
  const shouldUseNewAnalytics = usePackagePageV2Analytics();
  const isPostSearch = searchQuery && !isEmpty(searchQuery);

  const initialiseVariables = () => {
    let vars = { ...tile };

    if (tile.featuredProperty) {
      vars = {
        ...tile,
        cheapestOrigin: tile.featuredProperty.cheapestOrigin,
        nights: tile.featuredProperty.nights,
        origins: tile.featuredProperty.propertyOrigins,
        tagline: tile.propertyContent.tagline,
        travellers: tile.featuredProperty.travellers,
      };
    } else if (tile.procuredOffer) {
      const leadInOffer = tile.procuredOffer.rooms?.[0].leadInOffers?.[0];

      vars = {
        ...tile,
        cheapestOrigin: tile.cheapestOrigin,
        displayTravellerTypes: leadInOffer.displayTravellerTypes,
        inclusions: prependFlightCampaignInclusion(
          leadInOffer.inclusions,
          flightBadgeText,
          flightBadgeTerms,
        ),
        nights: leadInOffer.minNumberOfNights,
        routes: tile.procuredOffer.routes,
        travellers: leadInOffer.travellers,
      };
    }

    const originPrice =
      vars.routes?.filter((op) => op.origin === searchQuery?.originCode)[0] ??
      null;

    const { origin, price } = originPrice ||
      vars.cheapestOrigin || { price: {} };

    vars = {
      ...vars,
      origin,
      price,
    };

    // Overwrite the tile details if the user has performed a search
    if (isPostSearch) {
      const charges = tile.propertyContent.packageOffer?.charges || {};

      const departingOrigin = getOriginName(searchQuery.originCode);

      vars = {
        ...vars,
        departingOrigin,
        nights: getNights(searchQuery),
        price: isEmpty(charges) ? price : charges,
        travellers: searchQuery,
        valuedAtTotal: 0,
      };
    }

    return vars;
  };

  const {
    departingOrigin,
    displayTravellerTypes,
    inclusions,
    nights,
    origin,
    price,
    tagline,
    travellers = 0,
  } = initialiseVariables();

  const { perAdult, perTraveller, total, valuedAtTotal } = price;

  const perTravellerRate = priceForAdult ? perAdult : perTraveller;
  const hasInclusions = inclusions && inclusions.length > 0;

  const procuredOffer = tile.procuredOffer;

  // eslint-disable-next-line max-len
  const pillText = procuredOffer
    ? getCampaignEndMessage({
        dateRange: procuredOffer?.saleDateRange.end,
        extended: procuredOffer?.extended,
      })
    : tile.featuredProperty.pillText;

  const onViewPackageDetailsAndTerms = (overrideProps) => (close) => (
    <ViewDetailsModal
      close={close}
      title={`Holiday package to ${tile.propertyContent.destination.title}
                                    at ${tile.propertyContent.name}`}
      subtitle={procuredOffer?.name}
      terms={procuredOffer?.terms}
      inclusions={inclusions}
      routes={procuredOffer?.routes}
      destinationCode={
        tile.propertyContent.destination.searchConfig?.destinationCode
      }
      travelDateRange={procuredOffer?.travelDateRange}
      minNights={nights}
      {...overrideProps}
    />
  );

  const flightInclusion = createFlightCampaignInclusion({
    title: flightBadgeText,
    terms: flightBadgeTerms,
  });

  const { packageOptions } = getPackageOptions({
    procuredOffer: tile.procuredOffer,
    originCode,
    nights,
    title: flightBadgeText ? `${flightBadgeText} Deal` : undefined,
    commonInclusions: flightInclusion ? [flightInclusion] : undefined,
  });

  const shouldShowViewOtherOptions = packageOptions.length > 1 && !isPostSearch;

  const handleViewAllOptions = (event, show) => {
    if (pageName && shouldUseNewAnalytics) {
      propertyCardViewAllOptionsDataLayerEvent({ pageName });
    }
    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: 'View all options',
      });
    }
    event.preventDefault();

    show();
  };

  const hideCampaignOnPackagePage =
    !!campaignPromoCode && !flightBadgeText && !!isPostSearch;

  const href = getPropertyLink({
    propertyContent: tile.propertyContent,
    searchQuery: {
      ...searchQuery,
      originCode,
    },
    packageOffer: tile.propertyContent.packageOffer,
    destinationName: tile.propertyContent.destination.title,
    noCampaignPackageOptions: hideCampaignOnPackagePage,
    ...(isPostSearch && { packageOption: BEST_RATE_ID }),
  });

  const ctaLabel = isPostSearch ? 'Select package' : 'Check availability';

  const onClickCTA = (event) => {
    event.preventDefault();

    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: 'Select package',
        url: href?.pathname,
      });
    }

    const packageOptionAvailable =
      packageOptions.length >= 1 ? packageOptions[0] : undefined;

    if (ctaLabel && pageName && shouldUseNewAnalytics) {
      propertyCardCtaDataLayerEvent({ pageName, actionName: ctaLabel });
    }

    if (packageOptionAvailable) {
      let bestPackageId;

      // pre-search: use the first package option
      if (!isPostSearch) {
        bestPackageId = packageOptionAvailable?.id;
      }
      // post-search: use 'best-rate' unless offerId matches packageId,
      // in which case, we use that offer's inclusionSetId
      else {
        bestPackageId = 'best-rate';
        let packageOfferId = tile.packageOffer?.hotelOffer?.id;
        procuredOffer.rooms.forEach((room) => {
          const matchingLeadIn = room.leadInOffers.find((leadOffer) => {
            return leadOffer.id === packageOfferId;
          });
          matchingLeadIn && (bestPackageId = matchingLeadIn?.inclusionSetId);
        });
      }

      push({
        pathname: `/destinations/${tile.propertyContent.destination.name}/properties/${tile.propertyContent.id}/package`,
        query: {
          ...query,
          packageOption: bestPackageId,
          ...(hideCampaignOnPackagePage && { noCampaign: 'true' }),
        },
      });
    } else {
      push(href);
    }
  };

  const images = useMemo(() => tile.propertyContent.images, [tile]);

  return (
    <>
      <Flex
        data-testid="PROCURED_OFFER"
        bg="background.card"
        flexDirection={['column', null, 'row']}
        boxShadow="light"
        mt={pillText ? '5' : '0'}
        minHeight="564px"
        borderRadius="md"
        border="1"
        borderColor="lightGrey"
        overflow="hidden"
      >
        <Box position="absolute">
          {pillText && (
            <StyledPromoPillWrapper>
              <PromoPill
                message={pillText}
                icon={procuredOffer && 'earlyAccess'}
              />
            </StyledPromoPillWrapper>
          )}
        </Box>
        <Box
          height={['200px', null, '640px']}
          width={[null, null, '660px']}
          position="relative"
        >
          {flightBadgeText && (
            <Box position="absolute" zIndex="1" right={16} top={16}>
              <Badge
                data-testid="BADGE"
                text={flightBadgeText + '^'}
                fontWeight="700"
                fontSize="xxs"
                backgroundColor="ui.badgeBackground"
                padding="4px 8px"
              />
            </Box>
          )}
          <Skeleton height="100%" lineHeight="unset">
            <ImageGallery
              images={images}
              height={['200px', null, '640px']}
              mediaCounterStyle="counter"
            />
          </Skeleton>
        </Box>
        <Flex flex="1" maxHeight={[null, null, '640px']}>
          <Flex
            pt={['4', null, '6']}
            pb="6"
            flexDirection="column"
            justifyContent="space-between"
            flex="1"
          >
            <Box px={['4', null, '6']}>
              <Skeleton width="60px" />
              <Skeleton>
                <Box>
                  <Box mb={4}>
                    <PropertyTitle
                      destinationName={tile.propertyContent.destination.title}
                      propertyName={tile.propertyContent.name}
                      rating={tile.propertyContent.rating}
                      ratingType={tile.propertyContent.ratingType}
                      tags={tile.propertyContent.tags}
                      tagline={tile.procuredOffer?.offerDescription}
                    />
                  </Box>
                  <Box
                    minHeight={
                      {
                        [TILE_TYPE.FEATURED_PROPERTY]: [null, null, '100px'],
                        [TILE_TYPE.PROCURED_OFFER]: [null, null, '160px'],
                      }[tile.type]
                    }
                  >
                    {hasInclusions ? (
                      <>
                        <DealInclusions
                          inclusions={inclusions}
                          shownInclusions={4}
                          hideSubtitle
                        />
                        <ToggleContent
                          toggle={(show) => (
                            <LinkButton
                              noPadding
                              mb={[2, null, null]}
                              fontSize="sm"
                              onClick={(e) => {
                                if (pageName && shouldUseNewAnalytics) {
                                  propertyCardViewPackageDetailsDataLayerEvent({
                                    pageName,
                                  });
                                }
                                e.preventDefault();
                                show();
                              }}
                            >
                              <Text bold fontSize={['xs', null, 'sm']} link>
                                View package details and terms
                              </Text>
                            </LinkButton>
                          )}
                          content={onViewPackageDetailsAndTerms()}
                        />
                      </>
                    ) : (
                      <ReadMore
                        maxWidth={[null, null, '400px']}
                        showMoreText="Show more"
                        showLessText="Show less"
                        displayIcons
                        lines={3}
                      >
                        {tagline && <Text>{tagline}</Text>}
                      </ReadMore>
                    )}
                  </Box>
                </Box>
              </Skeleton>
            </Box>
            <Hide md lg>
              <Box px={['4', null, '6']} mb={4}>
                <Divider />
              </Box>
            </Hide>
            <Box px={['4', null, '6']} mt={[0, null, 4]}>
              <Skeleton width="30px">
                {isPostSearch ? (
                  <PackageHeroSummary
                    showNights
                    searchQuery={searchQuery}
                    exact
                  />
                ) : (
                  <PackageHeroSummary showNights nights={nights} exact />
                )}
              </Skeleton>
              {total && travellers && (
                <Skeleton width="100px">
                  <StyledPriceTotal mt="2">
                    {hidePerPersonPrice ? (
                      <PricePerTraveller
                        hidePerPersonPrice={hidePerPersonPrice}
                        perTraveller={perTraveller}
                        total={total}
                        travellers={travellers}
                      />
                    ) : (
                      <PricePerTraveller
                        displayTravellerTypes={displayTravellerTypes}
                        perAdult={perAdult}
                        perTraveller={
                          hidePriceSubtext ? undefined : perTravellerRate
                        }
                        priceForAdult={priceForAdult}
                        total={total}
                        travellers={isPostSearch ? searchQuery : travellers}
                        valuedAtTotal={valuedAtTotal}
                      />
                    )}
                  </StyledPriceTotal>
                </Skeleton>
              )}

              {(!searchQuery || isEmpty(searchQuery)) && (
                <Skeleton width="100px">
                  <Box
                    textAlign="right"
                    color="text.body"
                    mt={2}
                    fontSize={['xxs', null, 'sm']}
                  >
                    {`Departing ${departingOrigin ?? origin?.name}`}
                  </Box>
                </Skeleton>
              )}

              <Flex
                flexDirection={['column', null, 'row-reverse']}
                gap="16px"
                mt={4}
              >
                <Flex flex="1 0 0">
                  <Skeleton type="Button" width={['100%', null, '254px']}>
                    <Button
                      onClick={onClickCTA}
                      outline
                      data-testid="CTA"
                      nowrap
                      block={[true, null, shouldShowViewOtherOptions]}
                      width={[
                        0,
                        null,
                        !shouldShowViewOtherOptions ? '254px' : null,
                      ]}
                      marginLeft={[
                        null,
                        null,
                        !shouldShowViewOtherOptions ? 'auto' : null,
                      ]}
                    >
                      {ctaLabel}
                    </Button>
                  </Skeleton>
                </Flex>

                {shouldShowViewOtherOptions && (
                  <ToggleContent
                    toggle={(show) => (
                      <Flex flex="1 0 0">
                        <Skeleton type="Button" width={['100%', null, '254px']}>
                          <Button
                            onClick={(event) =>
                              handleViewAllOptions(event, show)
                            }
                            variant="secondary"
                            data-testid="VIEW_ALL_OPTIONS_CTA"
                            nowrap
                            block
                          >
                            View all options
                          </Button>
                        </Skeleton>
                      </Flex>
                    )}
                    content={(close) => (
                      <AllPackageOptionsModal
                        onClose={close}
                        viewPackageDetailsAndTermsContent={
                          onViewPackageDetailsAndTerms
                        }
                        tile={tile}
                        property={tile.propertyContent}
                        packageOptions={packageOptions}
                      />
                    )}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Box mt={2}>
        <ProcuredOfferFooter
          propertyContent={tile.propertyContent}
          procuredOffer={tile.procuredOffer}
          nights={nights}
          departingOrigin={departingOrigin}
          origin={origin}
          inclusions={inclusions}
        />
      </Box>
    </>
  );
};

PropertyCard.propTypes = {
  flightBadgeText: PropTypes.string,
  flightBadgeTerms: PropTypes.string,
  hidePerPersonPrice: PropTypes.bool,
  hidePriceSubText: PropTypes.bool,
  priceForAdult: PropTypes.bool,
  searchQuery: PropTypes.shape(SearchQuery),
  Skeleton: PropTypes.func.isRequired,
  tile: PropTypes.shape(Tile).isRequired,
};

const PropertyCardWithSkeleton = withSkeleton(({ loading }) => loading)(
  PropertyCard,
);

export default PropertyCardWithSkeleton;
